import React from 'react';
import _ from 'underscore';

import {
    buildColorAccessor, buildFirstThresholdMeasureAccessor, buildMeasureAccessor, buildTimestampAccessor
} from '@tableau/metrics-engine';
import {
    ActivitySpinnerSize, ActivitySpinnerWidget, DialogPositioningBehavior, DialogProps, DialogType,
    DialogWidget, HorizontalOriginOption, TextFieldWidget, VerticalOriginOption
} from '@tableau/widgets';

import { ProfilePictureStyles } from '../../app/content/styles/ProfilePictureStyles';
import { UserProfilePicture } from '../../app/content/UserProfilePicture';
import { Formatters, Messages } from '../../codegen/Localize';
import { ThumbnailImage } from '../../common/react/components/ThumbnailImage';
import { MetricSparklineAndPill } from '../../metrics/components/MetricSparklineAndPill';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ListResult } from '../../ts/fetchers/ListResult';
import { KeydownOutsideDetector } from '../../ts/helpers/KeydownOutsideDetector';
import { Keys } from '../../ts/helpers/Keys';
import { MousedownOutsideDetector } from '../../ts/helpers/MousedownOutsideDetector';
import { FlowListItemInfo } from '../../ts/models/FlowInfo';
import { IDataRoleInfo } from '../../ts/models/IDataRoleInfo';
import { IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { ILensInfo } from '../../ts/models/ILensInfo';
import { IProjectInfo } from '../../ts/models/IProjectInfo';
import { IViewInfo } from '../../ts/models/IViewInfo';
import { IWorkbookInfo } from '../../ts/models/IWorkbookInfo';
import { setupUIBindingForReactFunctionalComponent } from '../common/UIBindingForReact';
import {
    CatalogOmniboxItem,
    IMetricInfoForOmni,
    IPublishedConnectionInfoForOmni,
    IUserInfoForOmni,
    MenuItemHeaderInfo,
    OmniBoxItemType,
    OmniboxMenuItemType
} from '../models/Types';
import { OmniboxViewModel, shouldHorizontalDisplayItem } from '../viewModels/OmniboxViewModel';

const localizationSentinel = '@!@!@!@!@!@!Omni@!@!@!@!@!@!';
export const SearchResultElementId = 'OmniboxSearchResult';
export const SearchResultItemIdPrefix = 'OmniboxSearchResult-';

export type OmniboxOwnProps = {
    /*
     * This prop is used for integration with VizViewerPageHeader. That component has a toggle button to show/hide omnibox.
     * If the omnibox is closed by the toggle button, the focus should remain in the toggle button.
     * Or if the omnibox is closed by pressing ESC in it, the focus should remain in the toggle button.
     * Omnibox use this prop to communicate with VizViewerPageHeader.
    */
    closedWithFocus: () => void;
    /*
     * This prop is used for integration with VizViewerPageHeader. That component has a toggle button to show/hide omnibox.
     * If the omnibox is closed because the whole JavaScript window loses focus e.g. the user is in a Viz viewer page and searches something to pop up the
     * search result, then the user clicks the viz which is inside an iFrame.
     * In this case, the whole omnibox should be close and the toggle button should not hold the focus.
     * Omnibox use this prop to communicate with VizViewerPageHeader.
    */
    closedWithoutFocus: () => void;
    shouldOpenWithFocusInSearchTextbox: boolean;
    isSearchOpen?: boolean;
    viewModel?: OmniboxViewModel;
    isSearchInputBoxActive: (isActive: boolean) => void;
}

export type OmniboxStateProps = {
    siteRole: string;
}

export type OmniboxProps = OmniboxStateProps & OmniboxOwnProps;

/* Note: the view model of Omnibox is directly instantiated below as a singleton. It's not a normal way to do it in MVVM. It won't 
work if there's multiple Omniboxes in the UI.
The reason it's done in this way here is that the parent component of the Omnibox is not a MVVM implementation and currently there's 
only one Omnibox in the UI.
A suggested way to instantiate a view model should be:
1. If the parent of the view model is also an MVVM implemenation, the parent view model should instantiate the current view model.
2. If the parent of the view model is not an MVVM implementation, an HOC of current component should be created. The HOC component should 
create the instance of the view model and 
work as an intermediate layer between the parent component and the current component. Here is an example based on Omnibox.

// SomeParent is the parent which renders Omnibox. It talks to OmniboxMVVM component instead of directly talking to Omnibox view component.
export function SomeParent(props: SomeParentProps): JSX.Element {
    return <OmniboxMVVM />;
}

// OmniboxMVVM is the HOC of Omnibox MVVM implementation. It's responsibility is to create the view model instance for 
each of MVVM instance.
export function OmniboxMVVM(props: OmniboxProps): JSX.Element {
    let viewModel=new OmniboxViewModel();
    return <Omnibox {...props, viewModel=viewModel}/>;
}

// Omnibox is the actual view component of the MVVM implementation.
export function Omnibox(props: OmniboxProps): JSX.Element {
    let viewModel=props.viewModel;    
    return <div>{viewModel.whatEverData}</div>;
} */
let viewModel = new OmniboxViewModel();

export function Omnibox(props: OmniboxProps): JSX.Element {
    const TextBoxName = 'omniboxTextBox';
    const textBoxElementRef = React.useRef(null);
    const currentHighlightedElementRef = React.useRef(null);
    const allresultsElementRef = React.useRef(null);

    if (props.viewModel) {
        viewModel = props.viewModel;
    }

    const sectionHeaderMap: Record<string, string> = {
        [OmniBoxItemType.views]: Messages.views(),
        [OmniBoxItemType.metrics]: Messages.metrics(),
        [OmniBoxItemType.lenses]: Messages.lenses(),
        [OmniBoxItemType.datasources]: Messages.datasources(),
        [OmniBoxItemType.workbooks]: Messages.workbooks(),
        [OmniBoxItemType.projects]: Messages.projects(),
        [OmniBoxItemType.users]: Messages.users(),
        [OmniBoxItemType.flows]: Messages.flows(),
        [OmniBoxItemType.dataRoles]: Messages.dataRoles(),
        [OmniBoxItemType.tables]: Messages.tables(),
        [OmniBoxItemType.databases]: Messages.databasesAndFiles(),
        [OmniBoxItemType.publishedConnections]: Messages.publishedConnections(),
    };

    viewModel.askingForCloseWithFocus = props.closedWithFocus;
    viewModel.askingForCloseWithoutFocus = props.closedWithoutFocus;

    setupUIBindingForReactFunctionalComponent(viewModel);

    React.useEffect(() => {
        viewModel.initialize(props.shouldOpenWithFocusInSearchTextbox);
        viewModel.siteRole = props.siteRole;

        return () => {
            viewModel.onUnmount();
        };
    }, []);

    const resultElementRef = React.useRef(null);
    const pressEnterSeeAllElementRef = React.useRef(null);

    let detachMousedownOutsideDetector: () => void;
    let detachKeydownOutsideDetector: () => void;

    React.useEffect(() => {
        props.isSearchInputBoxActive(viewModel.isInputboxActive);
    }, [viewModel.isInputboxActive]);

    React.useEffect(() => {
        const timeoutForScrollIntoViewUseEffectHook = setTimeout(() => {
            if (currentHighlightedElementRef?.current && allresultsElementRef?.current) {
                const menuItemHeight = currentHighlightedElementRef.current.getBoundingClientRect().height;
                const viewPortHeight = allresultsElementRef.current.clientHeight - menuItemHeight;
                const itemTop = currentHighlightedElementRef.current.getBoundingClientRect().top;
                const itemBottom = itemTop + menuItemHeight;

                // if next navigation item is the searchbox, like narrow down for the last element or narrow up for the second element
                // make all results section scrolltop be 0 as inputbox is always above all result section and inputbox is not part of all result section
                if (viewModel.currentNavigationItem.type === OmniBoxItemType.searchbox) {
                    allresultsElementRef.current.scrollTop = 0;
                    return;
                }

                const viewPortTop = 0;
                // checks if shuold scroll up
                const isMenuItemParticallyNotShown = itemTop - menuItemHeight <= viewPortTop;
                if (isMenuItemParticallyNotShown) {
                    allresultsElementRef.current.scrollTop = allresultsElementRef.current.scrollTop - menuItemHeight + itemTop;
                } else if (itemBottom > viewPortHeight) { //checks if should scroll down
                    allresultsElementRef.current.scrollTop = allresultsElementRef.current.scrollTop + itemBottom - viewPortHeight;
                }
            }
        });

        return () => {
            if (timeoutForScrollIntoViewUseEffectHook) {
                clearTimeout(timeoutForScrollIntoViewUseEffectHook);
            }
        };
    }, [viewModel.currentNavigationItem]);

    React.useEffect(() => {
        if (viewModel.searchResultUIVisible) {
            const timeoutForSearchResultUIUseEffectHook = setTimeout(() => {
                // This will close the search panel when clicking on an iframe
                // (NLP tab and the viz) because window will lose focus when clicking into the iframe
                window.addEventListener('blur', onWindowBlur);

                detachMousedownOutsideDetector = MousedownOutsideDetector.attach(
                    resultElementRef.current,
                    (element: JQuery) => 'Omnibox',
                    (downEvent: JQueryEventObject) => {
                        if (!(textBoxElementRef.current && textBoxElementRef.current.contains(downEvent.target))) {
                            viewModel.cancelSearch();
                        }
                    });

                detachKeydownOutsideDetector = KeydownOutsideDetector.attach(
                    resultElementRef.current,
                    (element: JQuery) => 'Omnibox',
                    (downEvent: JQueryEventObject) => {
                        if (!(textBoxElementRef.current && textBoxElementRef.current.contains(downEvent.target))) {
                            viewModel.cancelSearch();
                        }
                    }, [Keys.Enter, Keys.Space, Keys.Escape]);
            });

            return () => {
                window.removeEventListener('blur', onWindowBlur);
                if (timeoutForSearchResultUIUseEffectHook) {
                    clearTimeout(timeoutForSearchResultUIUseEffectHook);
                }

                if (detachMousedownOutsideDetector) {
                    detachMousedownOutsideDetector();
                }
                if (detachKeydownOutsideDetector) {
                    detachKeydownOutsideDetector();
                }
            };
        }
    }, [viewModel.searchResultUIVisible]);

    const dialogWidgetContent: JSX.Element = <>
        <div className='tb-omnibox-status' aria-live='polite' role='status'>
            {viewModel.statusForAccessibility}
            {renderPressEnterToSeeAllContent()}
        </div>
        <div className={'tb-omnibox-popup'} ref={resultElementRef}>
            {viewModel.showResults ? omniboxAllSearchResultsSection() : null}
            {viewModel.showNoResults ? omniboxNoResultsSection() : null}
            {viewModel.showPending ? omniboxPendingSection() : null}
        </div>
    </>;

    const dialogWidgetProps: DialogProps = {
        title: '',
        dialogBodyStyle: { margin: 0, padding: 0 },
        // minWidth need to be same as tb-omnibox-search-box-long width in omniboxReact.styl
        dialogStyle: { marginTop: '4px', minWidth: '560px' },
        type: DialogType.Uber,
        isModal: false,
        isGlassTransparent: true,
        shouldReturnFocusOnClose: false,
        handleGlassClick: () => { },
        onCloseRequest: () => { },
        testId: 'omnibox-popup',
        showCloseButton: false,
        showFooter: false,
        disableAutoFocus: true,
        positioningBehavior: DialogPositioningBehavior.Anchored,
        getAnchorClientRect: () => getTextBoxRect(),
        anchorOrigin: { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.bottom },
        children: dialogWidgetContent,
    };

    function setInputboxRef(element: HTMLElement | null): void {
        textBoxElementRef.current = element;
        currentHighlightedElementRef.current = viewModel.currentNavigationItem.type === OmniBoxItemType.searchbox ? element : null;
    }

    const paddingRightForInputText = 20;
    const pressEnterToSeeAllWidth = pressEnterSeeAllElementRef.current ? pressEnterSeeAllElementRef.current.clientWidth + paddingRightForInputText + 'px' : '0px';

    const searchBoxAccessibilityProps: React.AriaAttributes = {
        'aria-haspopup': 'listbox',
        'aria-expanded': viewModel.searchResultUIVisible,
        'aria-autocomplete': 'list',
        'aria-activedescendant': viewModel.currentNavigationItemIdentifier ? SearchResultItemIdPrefix + viewModel.currentNavigationItemIdentifier : ''
    };
    if (viewModel.searchResultUIVisible) {
        searchBoxAccessibilityProps['aria-controls'] = SearchResultElementId;
    }

    /* eslint-disable jsx-a11y/no-autofocus */
    return (<span className='tb-omnibox'>
        <div className='tb-omnibox-text-box'
        >
            <div ref={setInputboxRef} className={viewModel.isInputboxActive ? 'tb-omnibox-search-box tb-omnibox-search-box-long' : 'tb-omnibox-search-box'}>
                <TextFieldWidget
                    kind='search'
                    label={null}
                    name={TextBoxName}
                    testId='omnibox'
                    autoComplete={'off'}
                    placeholder={FeatureFlags.metricsSpaceEnabled ? Messages.omniboxInputboxPlaceHolder() : Messages.omniboxInputboxPlaceHolderNoMetrics()}
                    style={{ paddingRight: pressEnterToSeeAllWidth }}
                    onFocus={e => viewModel.onSearchBoxFocus()}
                    onBlur={e => viewModel.onSearchBoxBlur()}
                    autoFocus={props.shouldOpenWithFocusInSearchTextbox}
                    value={viewModel.searchText}
                    onKeyDown={(e) => handleSearchBoxKeyPress(e)}
                    onChange={e => viewModel.onSearchTextChange(e.target.value)}
                    role='combobox'
                    {...searchBoxAccessibilityProps}
                ></TextFieldWidget>
                {pressEnterToSeeAllSection()}
            </div>
            {viewModel.searchResultUIVisible ?
                <DialogWidget {...dialogWidgetProps}>
                </DialogWidget> : null}
        </div>
    </span>);

    function handleSearchBoxKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.keyCode) {
            case Keys.Escape:
                viewModel.onEscapePressed();
                break;
            case Keys.Enter:
                viewModel.onEnterPressed();
                break;
            case Keys.ArrowLeft:
                viewModel.onArrowLeftPressed(() => event.preventDefault());
                break;

            case Keys.ArrowRight:
                viewModel.onArrowRightPressed(() => event.preventDefault());
                break;
            case Keys.ArrowDown:
                viewModel.onArrowDownPressed(() => event.preventDefault());
                break;
            case Keys.ArrowUp:
                viewModel.onArrowUpPressed(() => event.preventDefault());
                break;
        }
    }

    function pressEnterToSeeAllSection(): JSX.Element | null {
        return !viewModel.searchText ? null :
            <div className='tb-omnibox-press-enter-to-see-all' ref={pressEnterSeeAllElementRef}>
                {renderPressEnterToSeeAllContent()}
            </div>;
    }

    function renderPressEnterToSeeAllContent() {
        const messageWithSentinel = Messages.omniboxPressToSeeAll({ enter: localizationSentinel });
        const messagePieces = messageWithSentinel.split(localizationSentinel);

        return <>
            <span>{messagePieces[0]}</span>
            <strong>{Messages.omniboxPressToSeeAllEnterPart()}</strong>
            <span>{messagePieces[1]}</span>
        </>;
    }

    function getTextBoxRect(): ClientRect {
        if (textBoxElementRef?.current) {
            return textBoxElementRef.current.getBoundingClientRect();
        }

        return {} as ClientRect;
    }

    function omniboxPendingSection(): JSX.Element {
        return <div className='tb-fill-centered tb-omnibox-pending-spinner'>
            <ActivitySpinnerWidget
                spinnerSize={ActivitySpinnerSize.Small}
                testId={'omnibox-pending'}
            />
        </div>;
    }

    function omniboxCatalogingErrorsSection(): JSX.Element {
        return <>{viewModel.showCatalogingError ? <div className='tb-omnibox-cataloging-error-section' data-tb-test-id='omnibox-cataloging-errors'>
            {Messages.catalogingErrorOmniMessage()}
        </div> : null}</>;
    }

    function omniboxNoResultsSection(): JSX.Element {
        return <div className='tb-omnibox-no-results' data-tb-test-id='omnibox-no-results'>
            <div className='tb-omnibox-no-results-icon'></div>
            <div className='tb-omnibox-no-results-text'>{Messages.noResultsFound()}</div>
            {omniboxCatalogingErrorsSection()}
        </div>;
    }

    function omniboxMenuItemsSection(): JSX.Element[] {
        return viewModel.sections.map((section, index) => omniboxSection(sectionHeaderMap[section.type], section.type, section.list, index));
    }

    function omniboxAllSearchResultsSection(): JSX.Element {
        const SpaceLeftWhenMaxHeight = 100;
        return (<div ref={allresultsElementRef} className='tb-omnibox-results' id={SearchResultElementId} role='listbox' style={{ maxHeight: (window.innerHeight - SpaceLeftWhenMaxHeight) }}>
            {omniboxMenuItemsSection()}
            {omniboxCatalogingErrorsSection()}
        </div>);
    }

    function omniboxSection(title: string, type: OmniBoxItemType, list: ListResult<OmniboxMenuItemType>, index: number): JSX.Element | null {
        return list.items.length ?
            <div key={index} className={'tb-omnibox-section tb-omnibox-section-' + type} aria-labelledby={'header-title-' + title} data-tb-test-id={'omnibox-result-section-' + type}>
                {omniboxSectionHeader(title, type, list)}
                {omniboxMenuItems(list, type)}
            </div>
            : null;
    }

    function omniboxSectionHeader(title: string, type: OmniBoxItemType, list: ListResult<OmniboxMenuItemType>): JSX.Element {
        const menuItemHeaderInfo = viewModel.currentNavigationItem.target as MenuItemHeaderInfo;
        const shouldHeaderBeHighlighted = menuItemHeaderInfo.headerType === type;

        const highlightedStyle = shouldHeaderBeHighlighted ? ' tb-highlighted' : '';

        /* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role */
        return <div ref={shouldHeaderBeHighlighted ? currentHighlightedElementRef : null}>
            <a
                className={'tb-omnibox-header' + highlightedStyle}
                href={viewModel.seeAllHref(type, viewModel.searchText)}
                onClick={() => viewModel.onResultHeaderSelected(type, list.total)}
                id={SearchResultItemIdPrefix + type.toString()}>
                <span className={'tb-omnibox-section-header-text'} id={'header-title-' + title} data-tb-test-id={'omnibox-result-section-name-' + type}>
                    {title}
                </span>
                <span className={'tb-omnibox-section-header-count'} data-tb-test-id={'omnibox-result-section-count-' + type}>
                    {Formatters.formatCount(list.total)}
                </span>
                {viewModel.showSeeAll(type) ? <span className='tb-omnibox-see-all' data-tb-test-id={'omnibox-result-section-see-all-' + type}>
                    {Messages.home_seeAll()}
                </span> : null}
            </a>
        </div>;
    }

    function getRenderItemContent(type: OmniBoxItemType, item: OmniboxMenuItemType): JSX.Element {
        switch (type) {
            case OmniBoxItemType.views:
            default:
                return omniboxMenuItemView(item as IViewInfo);
            case OmniBoxItemType.metrics:
                return omniboxMenuItemMetric(item as IMetricInfo);
            case OmniBoxItemType.workbooks:
                return omniboxMenuItemWorkbook(item as IWorkbookInfo);
            case OmniBoxItemType.datasources:
                return omniboxMenuItemDatasource(item as IDataSourceInfo);
            case OmniBoxItemType.flows:
                return omniboxMenuItemFlow(item as FlowListItemInfo);
            case OmniBoxItemType.projects:
                return omniboxMenuItemProject(item as IProjectInfo);
            case OmniBoxItemType.tables:
                return omniboxMenuItemTable(item as CatalogOmniboxItem);
            case OmniBoxItemType.databases:
                return omniboxMenuItemDatabase(item as CatalogOmniboxItem);
            case OmniBoxItemType.dataRoles:
                return omniboxMenuItemDataRole(item as IDataRoleInfo);
            case OmniBoxItemType.lenses:
                return omniboxMenuItemLens(item as ILensInfo);
            case OmniBoxItemType.users:
                return omniboxMenuItemUser(item as IUserInfoForOmni);
            case OmniBoxItemType.publishedConnections:
                return omniboxMenuItemPublishedConnection(item as IPublishedConnectionInfoForOmni);
        }
    }

    function getMenuBoxItemStyle(type: OmniBoxItemType): string {
        switch (type) {
            case OmniBoxItemType.views:
                return 'tb-omnibox-menu-item tb-omnibox-menu-item-vertical';
            case OmniBoxItemType.workbooks:
                return 'tb-omnibox-menu-item tb-omnibox-menu-item-vertical tb-omnibox-menu-item-workbooks';
            case OmniBoxItemType.metrics:
                return 'tb-omnibox-menu-item tb-omnibox-menu-item-metrics tb-omnibox-menu-item-horizontal';
            case OmniBoxItemType.users:
                return 'tb-omnibox-menu-item tb-omnibox-menu-item-user tb-omnibox-menu-item-horizontal';
            default:
                return 'tb-omnibox-menu-item tb-omnibox-menu-item-horizontal';
        }
    }

    function omniboxMenuItems(list: ListResult<OmniboxMenuItemType>, type: OmniBoxItemType): JSX.Element {
        const listElements = list.items.map((item, index) => {
            const shouldItemBeHighlighted = item === viewModel.currentNavigationItem.target;
            const highlightedStyle = shouldItemBeHighlighted ? ' tb-highlighted' : '';
            /* eslint-disable jsx-a11y/role-supports-aria-props */
            /* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role */
            return !viewModel.isSectionItemPlaceholder(item) ? <a key={index}
                aria-selected={shouldItemBeHighlighted}
                role='option'
                id={SearchResultItemIdPrefix + item.id}
                onClick={() => viewModel.onResultItemSelected(type, list.total, index + 1, item.id)}
                className={'tb-omnibox-menu-item-container' + highlightedStyle}
                href={viewModel.itemHref(type, item)}
                title={viewModel.getMenuItemToolTip(type, item)}>
                <div
                    className={getMenuBoxItemStyle(type)}
                    ref={shouldItemBeHighlighted ? currentHighlightedElementRef : null}>
                    <div className='tb-omnibox-menu-item-highlight'></div>
                    <div className='tb-omnibox-menu-item-content'>
                        {getRenderItemContent(type, item)}
                    </div>
                </div>
            </a>
                : <div className={'tb-omnibox-menu-item-container'} key={index}>
                    <div className={getMenuBoxItemStyle(type)}>
                    </div>
                </div>;
        });

        const omniboxSectionHorizontalItems = 'tb-omnibox-section-items tb-horizontal-items';
        // make it automatically determines how many rows we want to render depends on the size of item and number
        // for view Item type, it will have max two lines of content
        if (type === OmniBoxItemType.views && listElements.length > 3) {
            return <>
                <div className={omniboxSectionHorizontalItems}>{listElements.slice(0, 3)}</div>
                <div className={omniboxSectionHorizontalItems}>{listElements.slice(3)}</div>
            </>;
        }

        // for metrics Item type, it will have max two lines of content
        if (type === OmniBoxItemType.metrics && listElements.length > 2) {
            return <>
                <div className={omniboxSectionHorizontalItems}>{listElements.slice(0, 2)}</div>
                <div className={omniboxSectionHorizontalItems}>{listElements.slice(2)}</div>
            </>;
        }

        return <div className={shouldHorizontalDisplayItem(type) ? omniboxSectionHorizontalItems : 'tb-omnibox-section-items tb-vertical-items'}>{listElements}</div>;
    }

    function omniboxMenuItemFavouriteSection(isFavorite: boolean, favoritesTotal: number): JSX.Element {
        return (
            <span className='tb-omnibox-menu-item-stat'>
                <span className={`tb-omnibox-menu-item-stat-icon ${isFavorite ? 'tb-favorite' : 'tb-not-favorite'}`}></span>
                <span className='tb-omnibox-menu-item-favorite-text' data-tb-test-id="omnibox-result-item-favorites-total" aria-hidden="true">{favoritesTotal}</span>
            </span>
        );
    }

    function omniboxMenuItemMetricsHitsCountSection(hitsTotal: number): JSX.Element {
        return (<span className={'tb-omnibox-menu-item-stat tb-omnibox-menu-item-stat-first'}>
            <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-hits-total">{Messages.metric_hits_count({
                COUNT: hitsTotal,
                COUNT_LOC: Formatters.formatCount(hitsTotal)
            })}</span>
        </span>);
    }

    function omniboxMenuItemView(item: IViewInfo): JSX.Element {
        return <>
            <ThumbnailImage url={item.thumbnailUrl} tbTestId='thumbnailImage' className={'tb-omnibox-popup-section-icon'}></ThumbnailImage>
            <div className='tb-omnibox-menu-item-details'>
                <div className={'tb-omnibox-menu-item-name'} data-tb-test-id="omnibox-result-item-name">{item.name}</div>
                <div className={'tb-omnibox-menu-item-owner'}>
                    <span>{Messages.owner()}</span>
                    <span aria-label={item.workbook.owner.displayName} className={'tb-omnibox-menu-item-displayName'}>{item.workbook.owner.displayName}</span>
                </div>
                <div className={'tb-omnibox-menu-item-stats'}>
                    {omniboxMenuItemMetricsHitsCountSection(item.usageInfo.hitsTotal)}
                    {omniboxMenuItemFavouriteSection(item.favorite, item.usageInfo.favoritesTotal)}
                </div>
            </div>
        </>;
    }

    function omniboxMenuItemWorkbook(item: IWorkbookInfo): JSX.Element {
        return <>
            <div className={'tb-omnibox-popup-section-icon tb-omnibox-popup-workbook'}>
                <div className='tb-omnibox-popup-workbook-decorator'></div>
                <div className='tb-omnibox-popup-workbook-icon-content'><ThumbnailImage url={item.thumbnailUrl} tbTestId='thumbnailImage'></ThumbnailImage></div>
            </div>
            <div className='tb-omnibox-menu-item-details'>
                <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
                <div className={'tb-omnibox-menu-item-owner'}>
                    <span>{Messages.owner()}</span>
                    <span aria-label={item.owner.displayName} className={'tb-omnibox-menu-item-displayName'}>{item.owner.displayName}</span>
                </div>
                <div className={'tb-omnibox-menu-item-stats'}>
                    {omniboxMenuItemMetricsHitsCountSection(item.usageInfo.hitsTotal)}
                    {omniboxMenuItemFavouriteSection(item.favorite, item.usageInfo.favoritesTotal)}
                </div>
            </div>
        </>;
    }

    function omniboxMenuItemMetric(item: IMetricInfoForOmni): JSX.Element {
        return <>
            <div className={'tb-omnibox-popup-metrics-icon'} data-tb-test-id="omnibox-metric-item">
                {item.response?.definition && <MetricSparklineAndPill
                    metricData={item.response}
                ></MetricSparklineAndPill>}
            </div>
            <div className='tb-omnibox-menu-item-details'>
                <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
                <div className={'tb-omnibox-menu-item-owner'}>
                    <span>{Messages.owner()}</span>
                    <span aria-label={item.owner.displayName} className={'tb-omnibox-menu-item-displayName'}>{item.owner.displayName}</span>
                </div>
                <div className={'tb-omnibox-menu-item-stats'}>
                    {omniboxMenuItemMetricsHitsCountSection(item.usageInfo.hitsTotal)}
                    {omniboxMenuItemFavouriteSection(item.favorite, item.usageInfo.favoritesTotal)}
                </div>
            </div>
        </>;
    }


    function omniboxMenuItemDatasource(item: IDataSourceInfo): JSX.Element {
        const datasourceCertifiedClassName = FeatureFlags.datasourceCertificationEnabled ? item.certificationDetails.isCertified ? 'tb-datasources-certified' : 'tb-datasources-not-certified'
            : 'tb-datasources-not-certified';

        return (
            <>
                <div className={`tb-omnibox-menu-item-icon ${datasourceCertifiedClassName}`}></div>
                <div className={'tb-omnibox-menu-item-details tb-omnibox-menu-item-details-horizontal'}>
                    <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
                    <div className={'tb-omnibox-menu-item-stats'}>
                        {omniboxMenuItemMetricsHitsCountSection(item.usageInfo.hitsTotal)}
                        <span className='tb-omnibox-menu-item-stat'>
                            <span className={'tb-omnibox-menu-item-stat-icon tb-workbook'}></span>
                            <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-workbook-count" aria-hidden="true">{item.connectedWorkbooksCount}</span>
                        </span>
                        {omniboxMenuItemFavouriteSection(item.favorite, item.usageInfo.favoritesTotal)}
                    </div>
                </div>
            </>
        );
    }

    function omniboxMenuItemFlow(item: FlowListItemInfo): JSX.Element {
        return (<>
            <div className='tb-omnibox-menu-item-icon tb-flows'></div>
            <div className={'tb-omnibox-menu-item-details tb-omnibox-menu-item-details-horizontal'}>
                <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
                <div className={'tb-omnibox-menu-item-stats'}>
                    <span className='tb-omnibox-menu-item-stat tb-omnibox-menu-item-stat-first'>
                        <span className={`tb-omnibox-menu-item-stat-icon ${item.favorite ? 'tb-favorite' : 'tb-not-favorite'}`}></span>
                    </span>
                </div>
            </div>
        </>);
    }

    function omniboxMenuItemProject(item: IProjectInfo): JSX.Element {
        return (<>
            <div className='tb-omnibox-menu-item-icon tb-projects'></div>
            <div className={'tb-omnibox-menu-item-details tb-omnibox-menu-item-details-horizontal'}>
                <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
                <div className={'tb-omnibox-menu-item-stats'}>
                    <span className='tb-omnibox-menu-item-stat tb-omnibox-menu-item-stat-first'>
                        <span className='tb-omnibox-menu-item-stat-icon tb-workbook'></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-workbook-count" aria-hidden="true">{item.workbookCount}</span>
                    </span>
                    <span className='tb-omnibox-menu-item-stat'>
                        <span className='tb-omnibox-menu-item-stat-icon tb-sheet'></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-sheet-count" aria-hidden="true">{item.viewCount}</span>
                    </span>
                    <span className='tb-omnibox-menu-item-stat'>
                        <span className='tb-omnibox-menu-item-stat-icon tb-datasource'></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-datasource-count" aria-hidden="true">{item.datasourceCount}</span>
                    </span>
                    {omniboxMenuItemFavouriteSection(item.favorite, item.usageInfo.favoritesTotal)}
                </div>
            </div>
        </>);
    }

    function omniboxMenuItemTable(item: CatalogOmniboxItem): JSX.Element {

        return (<>
            <div className={`tb-omnibox-menu-item-icon ${item.isCertified ? 'tb-tables-certified' : 'tb-tables'}`}>
            </div>
            <div className={'tb-omnibox-menu-item-details tb-omnibox-menu-item-details-horizontal'}>
                <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
                <div className={'tb-omnibox-menu-item-stats'}>
                    <span className='tb-omnibox-menu-item-stat tb-omnibox-menu-item-stat-first'>
                        <span className={'tb-omnibox-menu-item-stat-icon tb-workbook'} ></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-workbook-count" aria-hidden="true">{item.workbookCount}</span>
                    </span>
                    <span className='tb-omnibox-menu-item-stat'>
                        <span className={'tb-omnibox-menu-item-stat-icon tb-datasource'} ></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-datasource-count" aria-hidden="true">{item.datasourceCount}</span>
                    </span>
                </div>
            </div>
        </>);
    }

    function omniboxMenuItemDatabase(item: CatalogOmniboxItem): JSX.Element {
        return (<>
            <div className={`tb-omnibox-menu-item-icon ${item.isCertified ? 'tb-databases-certified' : 'tb-databases'}`}>
            </div>
            <div className={'tb-omnibox-menu-item-details tb-omnibox-menu-item-details-horizontal'}>
                <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
                <div className={'tb-omnibox-menu-item-stats'}>
                    <span className='tb-omnibox-menu-item-stat tb-omnibox-menu-item-stat-first'>
                        <span className={'tb-omnibox-menu-item-stat-icon tb-workbook'} ></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-workbook-count" aria-hidden="true">{item.workbookCount}</span>
                    </span>
                    <span className='tb-omnibox-menu-item-stat'>
                        <span className={'tb-omnibox-menu-item-stat-icon tb-datasource'} ></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-datasource-count" aria-hidden="true">{item.datasourceCount}</span>
                    </span>
                </div>
            </div>
        </>);
    }

    function omniboxMenuItemDataRole(item: IDataRoleInfo): JSX.Element {
        return (<>
            <div className='tb-omnibox-menu-item-icon tb-dataRoles' />
            <div className={'tb-omnibox-menu-item-details tb-omnibox-menu-item-details-horizontal'}>
                <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
            </div>
        </>);
    }

  function omniboxMenuItemLens(item: ILensInfo): JSX.Element {
    return (<>
      <div className='tb-omnibox-menu-item-icon tb-lenses' />
      <div className={'tb-omnibox-menu-item-details tb-omnibox-menu-item-details-horizontal'}>
        <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
      </div>
    </>);
  }

    function omniboxMenuItemUser(item: IUserInfoForOmni): JSX.Element {
        const profileImageSize = ProfilePictureStyles.Sizes.SmallIconSize;

        return (<>
            <UserProfilePicture profileDisplayName={item.displayName} imageUrl={item.userImageUrl} imageSize={profileImageSize} fontSize={ProfilePictureStyles.FontSizes.SmallIconFontSize} divStyle={{ marginRight: '8px' }} />
            <div className={'tb-omnibox-menu-item-details'}>
                <div className='tb-omnibox-menu-item-name'>
                    <span className='tb-omnibox-menu-item-displayname' data-tb-test-id="omnibox-result-item-name">{item.displayName}</span>
                </div>
                <div className='tb-omnibox-menu-item-username' data-tb-test-id="omnibox-result-item-username">{item.username}</div>
                {item.ownedItemCounts ? <div className={'tb-omnibox-menu-item-stats'}>
                    <span className='tb-omnibox-menu-item-stat tb-omnibox-menu-item-stat-first'>
                        <span className={'tb-omnibox-menu-item-stat-icon  tb-workbook'} ></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-workbook-count" aria-hidden="true">{item.ownedItemCounts.workbookCount}</span>
                    </span>
                    <span className='tb-omnibox-menu-item-stat'>
                        <span className={'tb-omnibox-menu-item-stat-icon tb-sheet'} ></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-view-count" aria-hidden="true">{item.ownedItemCounts.viewCount}</span>
                    </span>
                    <span className='tb-omnibox-menu-item-stat'>
                        <span className={'tb-omnibox-menu-item-stat-icon tb-datasource'} ></span>
                        <span className='tb-omnibox-menu-item-stat-text' data-tb-test-id="omnibox-result-item-datasource-count" aria-hidden="true">{item.ownedItemCounts.datasourceCount}</span>
                    </span>
                </div> : null}
            </div>
        </>);
    }

    function omniboxMenuItemPublishedConnection(item: IPublishedConnectionInfoForOmni): JSX.Element {

        return (
            <>
                <div className={'tb-omnibox-menu-item-icon tb-publishedConnections'}></div>
                <div className={'tb-omnibox-menu-item-details tb-omnibox-menu-item-details-horizontal'}>
                    <div className='tb-omnibox-menu-item-name' data-tb-test-id="omnibox-result-item-name">{item.name}</div>
                    <div className={'tb-omnibox-menu-item-stats'}>
                        <span className='tb-omnibox-menu-item-stat tb-omnibox-menu-item-stat-first'>
                            <span className={`tb-omnibox-menu-item-stat-icon ${item.favorite ? 'tb-favorite' : 'tb-not-favorite'}`} />
                        </span>
                    </div>
                </div>
            </>
        );
    }

    function onWindowBlur() {
        window.removeEventListener('blur', onWindowBlur);
        viewModel.onWindowBlurred();
    }
}
